import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { gqlRequest } from "../lib/gql";
import { DELETE_PLACE, PLACE_DETAIL, UPDATE_PLACE, type PlaceDetailResult, type RouteRef } from "../lib/queries";
import { Compass, MapPin, Pencil } from "lucide-react";
import { ACTIVITY_ICONS, formatDistance, formatDuration, PLACE_TYPE_ICONS, PLACE_TYPES, type PlaceType } from "../lib/format";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { EntityTabs } from "../components/EntityTabs";
import { Avatar } from "../components/Avatar";
import { ReportButton } from "../components/ReportButton";
import { SavePlaceButton } from "../components/SavePlaceButton";
import { ViewOnMapButton } from "../components/ViewOnMapButton";
import { EmptyState, ErrorBox, Field, Select, Spinner, TextInput } from "../components/ui";
import { Button } from "@/components/ui/button";
import { MiniMap } from "../map/MiniMap";
import { toFeature } from "../lib/geo";

type RouteSort = "default" | "name" | "distance" | "ascent";

export function PlacePage({ id }: { id: string }) {
  const { personId } = useAuth();
  const { t, labelizeT, entityName } = useT();
  const { data, isLoading, error } = useQuery({
    queryKey: ["place", id],
    queryFn: () => gqlRequest<PlaceDetailResult>(PLACE_DETAIL, { id }),
  });
  const [routeSort, setRouteSort] = useState<RouteSort>("default");

  if (isLoading) return <Spinner />;
  if (error) return <ErrorBox error={error} />;
  const place = data?.place;
  if (!place) return <EmptyState>{t("place.notFound")}</EmptyState>;

  // Routes near this place: curated links first, then the computed
  // track_place cache (track passes within the type's threshold), deduped.
  const nearby = new Map<string, { route: RouteRef; distanceM: number | null; curated: boolean }>();
  for (const rp of place.routePlacesByPlaceId.nodes) {
    nearby.set(rp.route.id, { route: rp.route, distanceM: null, curated: true });
  }
  for (const tp of place.trackPlacesByPlaceId.nodes) {
    for (const rt of tp.track.routeTracksByTrackId.nodes) {
      if (rt.role === "MAIN" && !nearby.has(rt.route.id)) {
        nearby.set(rt.route.id, { route: rt.route, distanceM: tp.distanceM, curated: false });
      }
    }
  }
  // The list merges two connections (curated links + track_place cache), so
  // sorting is client-side over the merged result — a server orderBy can't
  // order across both. "default" keeps curated links first.
  const routes = [...nearby.values()];
  if (routeSort === "name") routes.sort((a, b) => a.route.name.localeCompare(b.route.name));
  // `|| 0`: both-null comparisons yield Infinity - Infinity = NaN.
  else if (routeSort === "distance")
    routes.sort((a, b) => ((a.route.distanceM ?? Infinity) - (b.route.distanceM ?? Infinity)) || 0);
  else if (routeSort === "ascent")
    routes.sort((a, b) => ((b.route.ascentM ?? -Infinity) - (a.route.ascentM ?? -Infinity)) || 0);

  const PlaceIcon = PLACE_TYPE_ICONS[place.type] ?? MapPin;
  // UI gate only — RLS (place_owner_update/_delete) is what actually enforces
  // that only the creator can change or remove the place.
  const isCreator = personId != null && place.createdBy === personId;

  const header = (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary">
        <PlaceIcon aria-hidden className="size-3.5" />
        {labelizeT(place.type)}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{entityName(place)}</h1>
        <div className="flex items-center gap-2">
          <ViewOnMapButton geometry={place.geom} />
          <SavePlaceButton placeId={place.id} />
          <ReportButton entityKind="PLACE" entityId={place.id} />
        </div>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {place.elevation != null ? `${place.elevation} m` : ""}
        {place.placeRegionsByPlaceId.nodes.length > 0 && (
          <>
            {place.elevation != null ? " · " : ""}
            {place.placeRegionsByPlaceId.nodes.map((pr, i) => (
              <span key={pr.region.id}>
                {i > 0 && ", "}
                <Link
                  href={`/region/${pr.region.id}`}
                  className="text-primary transition-colors hover:underline"
                >
                  {entityName(pr.region)}
                </Link>
              </span>
            ))}
          </>
        )}
      </p>
      {place.createdByRow && (
        <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
          {t("place.addedBy")}
          <Avatar person={place.createdByRow} size="xs" />
          <Link
            href={`/person/${place.createdByRow.id}`}
            className="font-medium text-primary hover:underline"
          >
            {place.createdByRow.displayName ?? t("common.someone")}
          </Link>
        </p>
      )}
    </div>
  );

  const infoTab = (
    <div className="space-y-4">
      <MiniMap features={[toFeature(place.geom, { id: place.id })]} />
      <dl className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border bg-muted/50 p-3">
          <dt className="text-xs text-muted-foreground">{t("common.type")}</dt>
          <dd className="font-semibold text-foreground">{labelizeT(place.type)}</dd>
        </div>
        <div className="rounded-xl border bg-muted/50 p-3">
          <dt className="text-xs text-muted-foreground">{t("stat.elevation")}</dt>
          <dd className="font-semibold text-foreground">{place.elevation != null ? `${place.elevation} m` : "–"}</dd>
        </div>
      </dl>
      {isCreator && <OwnPlaceControls place={place} />}
    </div>
  );

  // F23 note: route pages get a community "Photos" tab sourced from linked
  // outings' photos. Doing the same here would need place → routes → outing
  // links → outings → photos (four connection levels on top of the already
  // merged curated+track_place route list) — too deep and heavy for one
  // document, so places deliberately skip photos for now.
  const routesTab = routes.length ? (
    <div className="space-y-3">
      <label className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
        {t("common.sort")}
        <Select className="!w-auto" value={routeSort} onChange={(e) => setRouteSort(e.target.value as RouteSort)}>
          <option value="default">{t("place.sortLinked")}</option>
          <option value="name">{t("common.name")}</option>
          <option value="distance">{t("place.sortDistance")}</option>
          <option value="ascent">{t("place.sortAscent")}</option>
        </Select>
      </label>
      <ul className="divide-y overflow-hidden rounded-xl border bg-card text-card-foreground shadow-card">
        {routes.map(({ route, distanceM, curated }) => {
          const RouteIcon = (route.activities[0] ? ACTIVITY_ICONS[route.activities[0]] : null) ?? Compass;
          return (
          <li key={route.id}>
            <Link
              href={`/route/${route.id}`}
              className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
            >
              <RouteIcon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-foreground">{entityName(route)}</span>
                <span className="block text-xs text-muted-foreground">
                  {route.activities.map(labelizeT).join(", ")}
                  {route.distanceM != null ? ` · ${formatDistance(route.distanceM)}` : ""}
                  {route.ascentM != null ? ` · ${Math.round(route.ascentM)} m ↑` : ""}
                  {route.durationMin ? ` · ${formatDuration(route.durationMin)}` : ""}
                  {curated ? ` · ${t("place.linked")}` : distanceM != null ? ` · ${t("place.passes", { distance: formatDistance(distanceM) })}` : ""}
                </span>
              </span>
            </Link>
          </li>
          );
        })}
      </ul>
    </div>
  ) : (
    <EmptyState>{t("place.noRoutes")}</EmptyState>
  );

  return (
    <EntityTabs
      header={header}
      tabs={[
        { key: "info", label: t("tabs.info"), content: infoTab },
        { key: "routes", label: t("tabs.routes"), badge: routes.length, content: routesTab },
      ]}
    />
  );
}

/**
 * Edit/delete controls for a place the viewer created (F41). Geometry is not
 * editable — moving a pin means deleting and re-adding from the map.
 */
function OwnPlaceControls({
  place,
}: {
  place: { id: string; name: string; type: PlaceType; elevation: number | null };
}) {
  const { t, labelizeT } = useT();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(place.name);
  const [type, setType] = useState<PlaceType>(place.type);
  const [elevation, setElevation] = useState(place.elevation != null ? String(place.elevation) : "");

  const update = useMutation({
    mutationFn: () =>
      gqlRequest(UPDATE_PLACE, {
        id: place.id,
        patch: {
          name: name.trim(),
          type,
          elevation: elevation.trim() === "" ? null : Math.round(Number(elevation)),
        },
      }),
    onSuccess: () => {
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ["place", place.id] });
    },
  });

  const remove = useMutation({
    mutationFn: () => gqlRequest(DELETE_PLACE, { id: place.id }),
    onSuccess: () => navigate("/"),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim() && !update.isPending) update.mutate();
  }

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setName(place.name);
            setType(place.type);
            setElevation(place.elevation != null ? String(place.elevation) : "");
            setEditing(true);
          }}
        >
          <Pencil aria-hidden className="size-4" />
          {t("place.editPlace")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            if (window.confirm(t("place.confirmDelete"))) remove.mutate();
          }}
          disabled={remove.isPending}
          className="border-destructive/30 text-destructive hover:border-destructive/50 hover:bg-destructive/10 hover:text-destructive"
        >
          {remove.isPending ? t("common.deleting") : t("common.delete")}
        </Button>
        {remove.error != null && <ErrorBox error={remove.error} />}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3 rounded-xl border bg-card p-4 text-card-foreground shadow-card">
      <Field label={t("common.name")}>
        <TextInput required value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("common.type")}>
        <Select value={type} onChange={(e) => setType(e.target.value as PlaceType)}>
          {PLACE_TYPES.map((pt) => (
            <option key={pt} value={pt}>
              {labelizeT(pt)}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("place.elevationOptional")}>
        <TextInput
          type="number"
          inputMode="numeric"
          min={-500}
          max={9000}
          value={elevation}
          onChange={(e) => setElevation(e.target.value)}
        />
      </Field>
      {update.error != null && <ErrorBox error={update.error} />}
      <div className="flex items-center gap-2">
        <Button type="submit" disabled={update.isPending || !name.trim()}>
          {update.isPending ? t("common.saving") : t("common.save")}
        </Button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          {t("common.cancel")}
        </button>
      </div>
    </form>
  );
}
